import { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Typography, Divider, Alert, RadioGroup, FormControlLabel, Radio, Checkbox,
  Stack, Box, CircularProgress, InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import {
  obterEvento, buscarResponsavel, buscarMatriculasResponsavel, inscreverAdmin,
} from "../../services/eventoService";

const moeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const soDigitos = (s) => (s || "").replace(/\D/g, "");

/**
 * Diálogo administrativo para inscrever um responsável (por CPF) em um evento,
 * seguindo os mesmos critérios do link público (categoria, oficinas e filhos).
 */
export default function InscricaoAdminDialog({ open, onClose, eventos = [], idEventoInicial = "", onInscrito }) {
  const [idEvento, setIdEvento] = useState("");
  const [ev, setEv] = useState(null);          // config completa do evento
  const [carregandoEv, setCarregandoEv] = useState(false);
  const [cpf, setCpf] = useState("");
  const [resp, setResp] = useState(null);      // responsável encontrado
  const [buscando, setBuscando] = useState(false);
  const [matriculas, setMatriculas] = useState(false); // false=n/a, null=carregando, []=nenhuma, [...]=lista
  const [matSels, setMatSels] = useState([]);
  const [valorSel, setValorSel] = useState("");
  const [opcSel, setOpcSel] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Reinicia tudo ao abrir.
  useEffect(() => {
    if (!open) return;
    setIdEvento(idEventoInicial ? String(idEventoInicial) : "");
    setEv(null); setCpf(""); setResp(null); setMatriculas(false); setMatSels([]);
    setValorSel(""); setOpcSel({}); setResultado(null);
  }, [open, idEventoInicial]);

  // Carrega a configuração do evento selecionado.
  useEffect(() => {
    if (!open || !idEvento) { setEv(null); return; }
    let ativo = true;
    setCarregandoEv(true); setResultado(null);
    obterEvento(idEvento)
      .then((r) => {
        if (!ativo) return;
        setEv(r.data);
        setValorSel(r.data.Pago && r.data.Valores?.length ? String(r.data.Valores[0].Id) : "");
        setOpcSel({});
      })
      .catch(() => { if (ativo) setEv(null); })
      .finally(() => { if (ativo) setCarregandoEv(false); });
    return () => { ativo = false; };
  }, [idEvento, open]);

  const buscarResp = useCallback(async () => {
    const d = soDigitos(cpf);
    if (d.length !== 11) { Swal.fire("Atenção", "Informe um CPF válido (11 dígitos).", "warning"); return; }
    setBuscando(true); setResp(null); setMatriculas(false); setMatSels([]); setResultado(null);
    try {
      const { data } = await buscarResponsavel(d);
      setResp(data);
      // Busca os filhos/matrículas se o evento cobra por aluno.
      if (ev && (ev.Pago || ev.CobrarPorFilho)) {
        setMatriculas(null);
        try {
          const rm = await buscarMatriculasResponsavel(d);
          const lista = rm.data.habilitado ? rm.data.matriculas : false;
          setMatriculas(lista);
          if (Array.isArray(lista)) setMatSels(lista.map((m) => m.Matricula));
        } catch { setMatriculas(false); }
      }
    } catch (e) {
      setResp(null);
      Swal.fire("Não encontrado", e?.response?.data?.mensagem || "Responsável não localizado.", "info");
    } finally { setBuscando(false); }
  }, [cpf, ev]);

  const toggleMat = (m) =>
    setMatSels((arr) => (arr.includes(m) ? arr.filter((x) => x !== m) : [...arr, m]));

  const valorBase = () => {
    if (!ev?.Pago || !valorSel) return 0;
    const v = (ev.Valores || []).find((x) => String(x.Id) === String(valorSel));
    return Number(v?.Valor || 0);
  };
  const adicionalFilhos = () => {
    if (!ev?.CobrarPorFilho || !Array.isArray(matriculas)) return 0;
    return matriculas
      .filter((m) => matSels.includes(m.Matricula) && m.Idade != null && m.Idade >= ev.IdadeMinimaCobranca)
      .reduce((s) => s + Number(ev.ValorFilho || 0), 0);
  };
  const total = () => valorBase() + adicionalFilhos();

  const confirmar = async () => {
    if (!resp) { Swal.fire("Atenção", "Busque o responsável pelo CPF.", "warning"); return; }
    if (ev.Pago && !valorSel) { Swal.fire("Atenção", "Escolha o valor da inscrição.", "warning"); return; }
    const faltando = (ev.Grupos || []).find((g) => g.Obrigatorio && !opcSel[g.Id]);
    if (faltando) { Swal.fire("Atenção", `Escolha uma opção em "${faltando.Nome}".`, "warning"); return; }
    if (ev.CobrarPorFilho && matSels.length === 0) {
      const c = await Swal.fire({
        title: "Inscrição sem aluno?",
        text: "Nenhum aluno selecionado. A inscrição será registrada somente no nome do responsável.",
        icon: "question", showCancelButton: true, confirmButtonText: "Continuar", cancelButtonText: "Voltar",
      });
      if (!c.isConfirmed) return;
    }
    const opcoes = Object.values(opcSel).filter(Boolean).map(Number);
    setEnviando(true);
    try {
      const { data } = await inscreverAdmin({
        Cpf: soDigitos(cpf), IdEvento: Number(idEvento),
        IdEventoValor: valorSel ? Number(valorSel) : null,
        Matriculas: matSels, Opcoes: opcoes,
      });
      setResultado(data.cobranca);
      onInscrito?.();
      Swal.fire("Pronto", data.mensagem, "success");
    } catch (e) {
      Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error");
    } finally { setEnviando(false); }
  };

  const opcoesAtivas = (g) => (g.Opcoes || []).filter((o) => o.Ativo);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Inscrever responsável</DialogTitle>
      <DialogContent dividers>
        <TextField select fullWidth size="small" label="Evento" value={idEvento}
          onChange={(e) => setIdEvento(e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="">Selecione o evento…</MenuItem>
          {eventos.map((e) => <MenuItem key={e.Id} value={String(e.Id)}>{e.Nome}</MenuItem>)}
        </TextField>

        {carregandoEv && <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}><CircularProgress size={18} /> Carregando evento…</Box>}

        {ev && (
          <>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
              <TextField fullWidth size="small" label="CPF do responsável" value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscarResp()}
                InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" color="action" /></InputAdornment> }} />
              <Button variant="outlined" onClick={buscarResp} disabled={buscando} sx={{ minWidth: 96, mt: 0.25 }}>
                {buscando ? "…" : "Buscar"}
              </Button>
            </Stack>

            {resp && (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                <b>{resp.Nome}</b> — {resp.Cpf}{resp.Email ? ` • ${resp.Email}` : ""}
              </Alert>
            )}

            {resp && !resultado && (
              <>
                {/* Categoria / valor */}
                {ev.Pago && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Valor da inscrição:</Typography>
                    <RadioGroup value={valorSel} onChange={(e) => setValorSel(e.target.value)}>
                      {ev.Valores.filter((v) => v.Ativo).map((v) => (
                        <FormControlLabel key={v.Id} value={String(v.Id)} control={<Radio />} label={`${v.Nome} — ${moeda(v.Valor)}`} />
                      ))}
                    </RadioGroup>
                  </>
                )}

                {/* Filhos / alunos */}
                {ev.CobrarPorFilho && (
                  <>
                    {ev.Pago && <Divider sx={{ my: 1.5 }} />}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Alunos (opcional):</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cada aluno {ev.IdadeMinimaCobranca}+ anos soma {moeda(ev.ValorFilho)}. Sem aluno marcado, a inscrição fica só no nome do responsável.
                    </Typography>
                    {matriculas === null ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}><CircularProgress size={18} /> Buscando alunos…</Box>
                    ) : Array.isArray(matriculas) && matriculas.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        {matriculas.map((m) => {
                          const cobra = m.Idade != null && m.Idade >= ev.IdadeMinimaCobranca;
                          return (
                            <FormControlLabel key={m.Matricula} sx={{ display: "flex" }}
                              control={<Checkbox checked={matSels.includes(m.Matricula)} onChange={() => toggleMat(m.Matricula)} />}
                              label={`${m.NomeAluno || "Aluno"}${m.Turma ? ` — Turma ${m.Turma}` : ""}${m.Idade != null ? ` — ${m.Idade} anos` : ""} • ${cobra ? `+ ${moeda(ev.ValorFilho)}` : "sem adicional"}`} />
                          );
                        })}
                      </Box>
                    ) : matriculas === false ? (
                      <Alert severity="info" sx={{ mt: 1.5 }}>Busca de alunos indisponível — inscrição em nome do responsável.</Alert>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1.5 }}>Nenhum aluno vinculado a este CPF — inscrição em nome do responsável.</Alert>
                    )}
                  </>
                )}

                {/* Oficinas / opções */}
                {(ev.Grupos || []).map((g) => (
                  <Box key={g.Id} sx={{ mt: 1.5 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{g.Nome}{g.Obrigatorio ? "" : " (opcional)"}</Typography>
                    <RadioGroup value={opcSel[g.Id] ? String(opcSel[g.Id]) : ""}
                      onChange={(e) => setOpcSel((s) => ({ ...s, [g.Id]: Number(e.target.value) }))}>
                      {opcoesAtivas(g).map((o) => (
                        <FormControlLabel key={o.Id} value={String(o.Id)} control={<Radio />}
                          label={`${o.Nome}${o.Vagas != null ? ` — ${o.Vagas} vaga(s)` : ""}`} />
                      ))}
                    </RadioGroup>
                  </Box>
                ))}

                {/* Total */}
                {(ev.Pago || ev.CobrarPorFilho) ? (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    {ev.Pago && <>Inscrição: {moeda(valorBase())}<br /></>}
                    {ev.CobrarPorFilho && <>Adicional dos alunos: {moeda(adicionalFilhos())}<br /></>}
                    <b>Total: {moeda(total())}</b>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 1.5 }}>Evento gratuito. Clique em confirmar para concluir.</Alert>
                )}
              </>
            )}

            {resultado && (
              <Alert severity={resultado.Pago || resultado.ValorTotal === 0 ? "success" : "warning"} sx={{ mt: 1 }}>
                {resultado.Pago || resultado.ValorTotal === 0
                  ? "Inscrição confirmada!"
                  : <>Inscrição registrada — total {moeda(resultado.ValorTotal)}. Confirme o pagamento na lista de inscritos (botão “Confirmar”) ou aguarde o PIX.</>}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{resultado ? "Fechar" : "Cancelar"}</Button>
        {ev && resp && !resultado && (
          <Button variant="contained" onClick={confirmar} disabled={enviando || matriculas === null}>
            {enviando ? "Enviando…" : "Confirmar inscrição"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
